import { FCFormDataType } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/types';
import { CompactUserResponse } from '@business/entities/account';
import { IDataItem } from 'dhx-diagram-package/codebase/types/ts-data';
import { ExtendedFile } from '../common';
import { FolderBreadcrumbResponse, FolderStructureResponse } from '../file';
import { UserId } from './../account/UserResponse';
import { BaseResponse } from './../common/BaseResponse';
import { TeamId } from '../team';

export type FolderId = string;
export type ItemId = string;

export interface LibraryFolderBreadcrumbResponse {
  id: string;
  name: string;
}

export interface LibraryItemsResponse {
  breadcrumbs: LibraryFolderBreadcrumbResponse[];
  items: (LibraryResponse | LibraryItemResponse)[];
}

export interface LibraryResponse extends BaseResponse {
  id: FolderId;
  name: string;
  type: LibraryItemType;
  isPolicy: boolean;
  isArticle: boolean;
  isChart: boolean;
  items: number;
  user: CompactUserResponse;
  parent?: LibraryResponse[];
  breadcrumbs?: LibraryFolderBreadcrumbResponse[];
  parentFolderId: FolderId;
  shares: LibraryItemShareItem[];
  documentType?: string;
  documentId?: string;
  fields?: FCFormDataType[];
  icon?: string;
  isSynced?: boolean; // Indicates if the folder is synced to AI98
  deleted?: number;
}

export interface JobDescriptionFunctionResponse extends BaseResponse {
  function: string;
  authorityLevel: JobAuthorify;
}

export interface JobDescriptionResponse extends BaseResponse {
  overview: string;
  youReports: string;
  reportYou: string;
  functions: JobDescriptionFunctionResponse[];
  education: string;
  experience: string;
  skills: string;
  physicalRequirements: string;
  kpi: string;
}

export type LibraryVersionId = string;
export interface LibraryItemVersionResponse extends BaseResponse {
  id: LibraryVersionId;
  title: string;
  content: IDataItem[];
}
export interface LibraryItemResponse extends BaseResponse {
  id: ItemId;
  name: string;
  content: string | JobDescriptionResponse | IDataItem[];
  type: LibraryItemType;
  user: CompactUserResponse;
  status: LibraryItemStatus;
  folder?: LibraryResponse[];
  files: ExtendedFile[];
  parentFolderId: FolderId;
  versions: LibraryItemVersionResponse[];
  shares: LibraryItemShareItem[];
  breadcrumbs?: FolderBreadcrumbResponse[];
  filePath: string;
  viewableContent: string;
  documentId?: string;
  documentType?: string;
  fields?: FCFormDataType[];
  icon?: string;
  isSynced?: boolean; // Indicates if the item is synced to AI98
  deleted?: number;
}

export enum LibraryItemShareType {
  item = 'item',
  folder = 'folder',
}

export interface LibraryItemShareItem {
  userId: UserId | null;
  teamId: TeamId | null;
  view: boolean;
  manage: boolean;
  type: LibraryItemShareType;
}

export interface LibraryItemAttachedFile {
  fileName: string;
  filePath: string;
}
export interface CompactLibraryItemResponse extends BaseResponse {
  id: ItemId;
  name: string;
  content: string | JobDescriptionResponse | IDataItem[];
  type: LibraryItemType;
  user: CompactUserResponse;
  status: LibraryItemStatus;
  parentFolderId: FolderId;
  files: ExtendedFile[];
  shares: LibraryItemShareItem[];
  filePath: string;
  documentId?: string;
  documentType?: string;
  requestId?: number;
  icon?: string;
  isSynced?: boolean;
  deleted?: number;
}

export type ItemRequest = Pick<LibraryItemResponse, 'name' | 'content' | 'type' | 'folder'>;

export enum LibraryItemStatus {
  public = 'public',
  private = 'private',
  draft = 'draft',
}

export enum LibraryItemType {
  folder = 'folder',
  //   article = 'article',
  policy = 'policy',
  job = 'job',
  howTo = 'howTo',
  orgChart = 'orgChart',
  flowChart = 'flowChart',
  file = 'file',
  custom = 'custom',
}

export interface CompactLibraryFolderResponse {
  id: FolderId;
  name: string;
}

export const LibraryItemStatuses = [LibraryItemStatus.public, LibraryItemStatus.private, LibraryItemStatus.draft];
export const LibraryItemTypes = [LibraryItemType.folder, LibraryItemType.howTo, LibraryItemType.policy];

export const LibraryItemStatusColor: Record<LibraryItemStatus, string> = {
  private: 'dark',
  public: 'success',
  draft: 'info',
};

export const LibraryItemTypeIcon: Record<LibraryItemType, string> = {
  folder: 'bx bx-folder',
  howTo: 'bx bx-book',
  policy: 'bx bx-file',
  job: 'bx bx-award',
  orgChart: 'bx bx-sitemap',
  flowChart: 'bx bx-transfer',
  file: 'bx bx-file',
  custom: 'bx bx-file',
};

export const LibraryItemTypeColor: Record<LibraryItemType, string> = {
  folder: 'primary',
  howTo: 'info',
  policy: 'secondary',
  job: 'dark',
  orgChart: 'dark',
  flowChart: 'dark',
  file: 'primary',
  custom: 'secondary',
};

export interface CompactLibrarySearchResponse {
  id: FolderId;
  name: string;
  type: LibraryItemType;
  documentType?: string;
  status: LibraryItemStatus;
  parentFolderId: FolderId;
  breadcrumbs?: LibraryFolderBreadcrumbResponse[];
}

export interface LibraryFolderStructureResponse {
  id: string;
  name: string;
  folders: FolderStructureResponse[];
  breadcrumbs?: FolderBreadcrumbResponse[];
}

export enum JobAuthorify {
  getApproval = 'getApproval',
  handle = 'handle',
  act = 'act',
}

export const JOB_DESCRIPTION_AUTHORITIES: Record<JobAuthorify, string> = {
  getApproval: 'Inform and then act (Get approval)',
  handle: 'Act and then inform (Handle, but inform)',
  act: 'Act (Just take care of it)',
};

export interface PublicLibraryItemResponse extends LibraryItemResponse {
  logo: string;
}
